import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { AppTranslationService } from './app-translation.service';
import { LocalStoreManager } from './local-store-manager.service';
import { DBkeys } from './db-keys';
import { Utilities } from './utilities';
import { environment } from '../../environments/environment';

interface UserConfiguration {
    language: string | null;
    homeUrl: string | null;
    themeId: number | null;
  }

@Injectable()
export class ConfigurationService {

    constructor(
        private localStorage: LocalStoreManager,
        private translationService: AppTranslationService) {

        this.loadLocalChanges();
    }

    set language(value: string | null) {
        this._language = value;
        this.saveToLocalStore(value, DBkeys.LANGUAGE);
        this.translationService.changeLanguage(value);
      }
      get language(): string {
        return this._language ?? ConfigurationService.defaultLanguage;
      }
    
      set themeId(value: number) {
        this._themeId = value;
        this.saveToLocalStore(value, DBkeys.THEME_ID);
      }
      get themeId() {
        return this._themeId ?? ConfigurationService.defaultThemeId;
      }
    
      set homeUrl(value: string | null) {
        this._homeUrl = value;
        this.saveToLocalStore(value, DBkeys.HOME_URL);
      }
      get homeUrl(): string {
        return this._homeUrl ?? ConfigurationService.defaultHomeUrl;
      }
    
    public static readonly appVersion: string = '6.0.0';

    // ***Specify default configurations here***
    public static readonly defaultLanguage: string = 'sv';
    public static readonly defaultHomeUrl: string = '/';
    public static readonly defaultThemeId: number = 1;

    public baseUrl = environment.baseUrl || Utilities.baseUrl();
    public loginUrl = environment.loginUrl;
    public googleClientId = environment.googleClientId;
    public facebookClientId = environment.facebookClientId;
    public microsoftClientId = environment.microsoftClientId;
    public fallbackBaseUrl = '"https://swedishwelcomeapp.azurewebsites.net';
    // ***End of defaults***

    private _language: string = null;
    private _homeUrl: string = null;
    private _themeId: number = null;
    private onConfigurationImported: Subject<void> = new Subject<void>();

    configurationImported$ = this.onConfigurationImported.asObservable();

    private loadLocalChanges() {
        if (this.localStorage.exists(DBkeys.LANGUAGE)) {
            this._language = this.localStorage.getDataObject<string>(DBkeys.LANGUAGE);
            this.translationService.changeLanguage(this._language);
        } else {
            this.resetLanguage();
        }

        if (this.localStorage.exists(DBkeys.THEME_ID)) {
            this._themeId = this.localStorage.getDataObject<number>(DBkeys.THEME_ID);
        }

        if (this.localStorage.exists(DBkeys.HOME_URL)) {
            this._homeUrl = this.localStorage.getDataObject<string>(DBkeys.HOME_URL);
        }

    }

    private saveToLocalStore(data: unknown, key: string) {
        setTimeout(() => this.localStorage.savePermanentData(data, key));
    }

    public import(jsonValue: string | null) {
        this.clearLocalChanges();

        if (jsonValue) {
            const importValue: UserConfiguration = Utilities.JsonTryParse(jsonValue);

            if (importValue.language != null) {
                this.language = importValue.language;
            }

            if (importValue.themeId != null) {
                this.themeId = importValue.themeId;
            }

            if (importValue.homeUrl != null) {
                this.homeUrl = importValue.homeUrl;
            }
        }

        this.onConfigurationImported.next();
    }

    public export(changesOnly = true): string {
        const exportValue: UserConfiguration = {
            language: changesOnly ? this._language : this.language,
            themeId: changesOnly ? this._themeId : this.themeId,
            homeUrl: changesOnly ? this._homeUrl : this.homeUrl
        };

        return JSON.stringify(exportValue);
    }

    public clearLocalChanges() {
        this._language = null;
        this._themeId = null;
        this._homeUrl = null;

        this.localStorage.deleteData(DBkeys.LANGUAGE);
        this.localStorage.deleteData(DBkeys.THEME_ID);
        this.localStorage.deleteData(DBkeys.HOME_URL);

        this.resetLanguage();
        this.clearUserConfigKeys();
    }

    private resetLanguage() {
        const language = this.translationService.useBrowserLanguage();

        if (language) {
            this._language = language;
        } else {
            this._language = this.translationService.useDefaultLanguage();
        }
    }

    private addKeyToUserConfigKeys(configKey: string) {
        const configKeys = this.localStorage.getDataObject<string[]>(DBkeys.USER_CONFIG_KEYS) ?? [];
    
        if (!configKeys.includes(configKey)) {
          configKeys.push(configKey);
          this.localStorage.savePermanentData(configKeys, DBkeys.USER_CONFIG_KEYS);
        }
      }
    
      private clearUserConfigKeys() {
        const configKeys = this.localStorage.getDataObject<string[]>(DBkeys.USER_CONFIG_KEYS);
    
        if (configKeys != null && configKeys.length > 0) {
          for (const key of configKeys) {
            this.localStorage.deleteData(key);
          }
    
          this.localStorage.deleteData(DBkeys.USER_CONFIG_KEYS);
        }
      }
    
      public saveConfiguration(data: unknown, configKey: string) {
        this.addKeyToUserConfigKeys(configKey);
        this.localStorage.savePermanentData(data, configKey);
      }
    
      public getConfiguration<T>(configKey: string, isDateType = false) {
        return this.localStorage.getDataObject<T>(configKey, isDateType);
      }
    
}
